import React, { useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Grid,
  Typography,
  Box,
  Button,
  Skeleton,
  TextField,
  IconButton,
  Menu,
  MenuItem
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { callAPI, Method } from "../../commonFunctions/CommonFunctions";
import {
  resetDataComponent,
  storeDataComponent
} from "../../StateManagement/actions";
import { useDispatch } from "react-redux";
import { Delete, Edit, MoreVert } from "@mui/icons-material";
import ReusableModal from "../Modal/WSModal";
import { useSnackbar } from "../Snackbar/SnackBarContext";

interface Source {
  url: string;
  pageSize: number;
  page: number;
  filter: any[];
  sort: any[];
  type?: "POST" | "GET";
}

interface Role {
  _id: string;
  role: string;
  rate: number;
}

interface RateCard {
  _id: string;
  name: string;
  roles: Role[];
}

interface CustomTableProps {
  id: string;
  source: Source;
  columns: { key: string; label: string }[];
  renderActions?: (row: any, index: number) => React.ReactNode;
  description?: string;
  handleUpdate: (id: string) => void;
  handleDelete: (id: string) => void;

  cardId: string | null;
  cardName: string;
  setEditedCardName: (name: string) => void;
  setEditingCardId: (id: string) => void;
}

const WSSmallGrid: React.FC<CustomTableProps> = ({
  id,
  cardId,
  cardName,
  renderActions,
  source,
  columns,
  description,
  handleUpdate,
  handleDelete,
  setEditedCardName,
  setEditingCardId
}) => {
  const dispatch = useDispatch();
  const { showMessage } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [rateCards, setRateCards] = useState<RateCard[]>([]);
  const [openRoleModal, setOpenRoleModal] = useState(false);
  const [roleForm, setRoleForm] = useState({ role: "", rate: 0 });

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuTarget, setMenuTarget] = useState<{
    cardId: string;
    roleIndex: number;
  } | null>(null);
  // const [menuIndex, setMenuIndex] = React.useState<number | null>(null);

  //edit / delete role or rate;
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const body = {
          page: source.page,
          pageSize: source.pageSize,
          filter: source.filter,
          sort: source.sort
        };

        const response = await callAPI(
          source.url,
          source.type === "GET" ? Method.GET : Method.POST,
          body
        );

        const result = response?.data || [];
        setRateCards(result);
        dispatch(storeDataComponent(id, result));
      } catch (error) {
        console.error("Failed to fetch data", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [source, dispatch]);

  const handleAddRole = async () => {
    if (!selectedCardId) return;

    const payload = {
      role: roleForm.role,
      rate: roleForm.rate
    };

    try {
      setLoading(true);
      const response = await callAPI(
        `rateCards/addRole/${selectedCardId}`,
        Method.PUT,
        payload
      );

      if (response.success) {
        showMessage(response.message || "Role added successfully", "success");
        dispatch(resetDataComponent("rateCardGrid"));
      }
    } catch (error: any) {
      showMessage(
        error.response?.data?.message || "Failed to add role",
        "error"
      );
    } finally {
      setLoading(false);
      setOpenRoleModal(false);
      handleMenuClose();
    }
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    roleIndex: number,
    cardId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuTarget({ cardId, roleIndex });
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuTarget(null);
  };

  const handleAction = async (action: string, row: Role, cardId: string) => {
    if (action === "Edit") {
      setOpenEditModal(true);
      setSelectedCardId(cardId);
      setSelectedRowId(row._id);
      setEditingRole(row);
    } else if (action === "Delete") {
      setLoading(true)
      try {
        const response = await callAPI(
          `rateCards/removeRole/${cardId}/${row._id}`,
          Method.PUT
        );

        if (response.success) {
          showMessage(
            response.message || "Role deleted successfully",
            "success"
          );
          dispatch(resetDataComponent("rateCardGrid"));
        }
      } catch (error: any) {
        showMessage(
          error.response?.data?.message || "Failed to delete role",
          "error"
        );
      } finally {
        setLoading(false)
        handleMenuClose();
      }
    }
  };

  const handleUpdateRole = async () => {
    if (!editingRole || !selectedCardId) return;

    try {
      setLoading(true)
      const response = await callAPI(
        `rateCards/updateRole/${selectedCardId}/${selectedRowId}`,
        Method.PUT,
        editingRole
      );

      if (response.success) {
        showMessage(response.message || "Role updated successfully", "success");
        dispatch(resetDataComponent("rateCardGrid"));
      }
    } catch (error: any) {
      showMessage(
        error.response?.data?.message || "Failed to update role",
        "error"
      );
    } finally {
      setOpenEditModal(false);
      handleMenuClose();
      setLoading(false)
    }
  };

  if (loading) {
    return (
      <Grid container>
        <Grid item xs={12} md={8} lg={6}>
          <Skeleton variant="text" width="40%" height={32} />
          <TableContainer component={Paper} sx={{ borderRadius: 2, mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell key={col.key}>
                      <Skeleton variant="text" width="80%" />
                    </TableCell>
                  ))}
                  {renderActions && (
                    <TableCell>
                      <Skeleton variant="text" width="40%" />
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {[...Array(3)].map((_, i) => (
                  <TableRow key={i}>
                    {columns.map((col) => (
                      <TableCell key={col.key}>
                        <Skeleton variant="text" width="70%" />
                      </TableCell>
                    ))}
                    {renderActions && (
                      <TableCell>
                        <Skeleton variant="circular" width={24} height={24} />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    );
  }

  if (!loading && rateCards.length === 0) {
    return (
      <Grid container>
        <Grid item xs={12} md={8} lg={6}>
          <Typography variant="body1" align="center" sx={{ py: 4 }}>
            No rate cards found.
          </Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      {rateCards.map((card, cardIndex) => (
        <Grid item xs={12} md={8} lg={6} key={cardIndex}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            {cardId === card._id ? (
              <TextField
                size="small"
                value={cardName}
                onChange={(e) => setEditedCardName(e.target.value)}
                onBlur={() => handleUpdate(card._id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleUpdate(card._id);
                }}
                autoFocus
              />
            ) : (
              <>
                <Typography variant="h6" fontWeight={600}>
                  {card.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditingCardId(card._id);
                    setEditedCardName(card.name);
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>

          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "transparent" }}>
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      sx={{ fontWeight: "bold", color: "#555" }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                  {renderActions && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {card.roles.map((row, index) => (
                  <TableRow key={index}>
                    {columns.map((col) => (
                      <TableCell key={col.key}>
                        {row[col.key as keyof Role]}
                      </TableCell>
                    ))}

                    <TableCell>
                      <>
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, index, card._id)}
                        >
                          <MoreVert />
                        </IconButton>

                        <Menu
                          anchorEl={anchorEl}
                          open={
                            !!menuTarget &&
                            menuTarget.cardId === card._id &&
                            menuTarget.roleIndex === index
                          }
                          onClose={handleMenuClose}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right"
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "right"
                          }}
                        >
                          <MenuItem
                            onClick={() => {
                              handleAction("Edit", row, card._id);
                              handleMenuClose();
                            }}
                            sx={{ fontSize: "14px" }}
                          >
                            Edit
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              handleAction("Delete", row, card._id);
                              handleMenuClose();
                            }}
                            sx={{ fontSize: "14px" }}
                          >
                            Delete
                          </MenuItem>
                        </Menu>
                      </>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Box p={2} display="flex" justifyContent="space-between">
              <Button
                variant="contained"
                size="small"
                color="warning"
                sx={{ backgroundColor: "#FF6B2C", textTransform: "none" }}
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedCardId(card._id);
                  setRoleForm({ role: "", rate: 0 });
                  setOpenRoleModal(true);
                }}
              >
                New Role
              </Button>

              <Button
                variant="contained"
                size="small"
                color="error"
                sx={{ backgroundColor: "crimson", textTransform: "none" }}
                startIcon={<Delete />}
                onClick={() => handleDelete(card._id)}
              >
                Delete Card
              </Button>
            </Box>
          </TableContainer>

          <Typography mt={3} variant="caption" color="textSecondary">
            {description}
          </Typography>
        </Grid>
      ))}

      <ReusableModal
        open={openRoleModal}
        onClose={() => setOpenRoleModal(false)}
        title="Add New Role"
        footer={
          <Button
            onClick={handleAddRole}
            variant="contained"
            size="small"
            color="warning"
            sx={{ backgroundColor: "#FF6B2C", textTransform: "none" }}
          >
            Save Role
          </Button>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Role"
              value={roleForm.role}
              onChange={(e) =>
                setRoleForm((prev) => ({ ...prev, role: e.target.value }))
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Rate"
              type="number"
              value={roleForm.rate}
              onChange={(e) =>
                setRoleForm((prev) => ({ ...prev, rate: +e.target.value }))
              }
            />
          </Grid>
        </Grid>
      </ReusableModal>

      <ReusableModal
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        title="Edit Role"
        footer={
          <Button
            onClick={handleUpdateRole}
            variant="contained"
            size="small"
            color="warning"
            sx={{ backgroundColor: "#FF6B2C", textTransform: "none" }}
          >
            Update Role
          </Button>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Role"
              value={editingRole?.role}
              onChange={(e) =>
                setEditingRole((prev) => ({ ...prev!, role: e.target.value }))
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Rate"
              type="number"
              value={editingRole?.rate}
              onChange={(e) =>
                setEditingRole((prev) => ({ ...prev!, rate: +e.target.value }))
              }
            />
          </Grid>
        </Grid>
      </ReusableModal>
    </Grid>
  );
};

export default WSSmallGrid;
